import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import {
  Integration,
  IntegrationCategory,
} from 'models/InternalIntegrationSetting';
import { Trans, useTranslation } from 'react-i18next';
import { hasExclusiveConnectionRequirement } from 'models/InternalIntegrationSetting.utils';
import { IntegrationMetadataStyled } from '../PropertyListItem.styles';
import usePropertyActiveIntegration from './usePropertyActiveIntegration';

const IntegrationMetadata = ({
  integration: { enumId, category, displayName, name },
  propertyUid,
  isPropertySupported,
  pagePropertyUids,
}: {
  integration: Integration;
  propertyUid: string;
  isPropertySupported: boolean;
  pagePropertyUids: string[];
}) => {
  const { t } = useTranslation();
  const {
    hasActiveIntegrationConnected,
    isConnectedToCurrentIntegration,
    account,
    activeIntegration,
    isLoading,
  } = usePropertyActiveIntegration({ enumId, propertyUid, pagePropertyUids });

  const integrationName = displayName ?? name;
  const shouldShowMetadata =
    hasExclusiveConnectionRequirement(category) && isPropertySupported;

  const getMetadata = () => {
    const isPaymentProcessor = category === IntegrationCategory.PAYMENTS;
    const isDynamicPricingTool =
      category === IntegrationCategory.DYNAMIC_PRICING;

    // no payment processor connected
    if (isPaymentProcessor && !hasActiveIntegrationConnected) {
      return (
        <p className="text-danger">
          {t('pageIntegrations.integrationDetails.noPaymentProcessor')}
        </p>
      );
    }

    // no dynamic pricing tool connected
    if (isDynamicPricingTool && !hasActiveIntegrationConnected) {
      return (
        <p className="text-danger">
          {t('pageIntegrations.integrationDetails.noDynamicPricingTool')}
        </p>
      );
    }

    // connected to the current integration
    if (isConnectedToCurrentIntegration) {
      if (account) {
        return (
          <p className="text-gray">
            {t('pageIntegrations.integrationDetails.integrationAccount', {
              integration: integrationName,
              account,
            })}
          </p>
        );
      }

      return (
        <p className="text-gray">
          <Trans
            i18nKey="pageIntegrations.integrationDetails.connectedToIntegration"
            values={{ integration: integrationName }}
            components={{ 1: <span /> }}
          />
        </p>
      );
    }

    // connected to another integration
    const otherIntegrationName =
      activeIntegration?.displayName ?? activeIntegration?.name;
    const otherIntegrationEnumId = activeIntegration?.enumId;

    return (
      <p className="text-gray">
        <Trans
          i18nKey="pageIntegrations.integrationDetails.connectedToIntegration"
          values={{ integration: otherIntegrationName }}
          components={{
            1: (
              <a
                href={`#/integrations/details/${otherIntegrationEnumId}`}
                target="_blank"
                rel="noreferrer"
                aria-label={`View ${otherIntegrationName} integration details`}
              />
            ),
          }}
        />
      </p>
    );
  };

  if (isLoading) {
    return (
      <IntegrationMetadataStyled>
        <LoadingPlaceholder width="100px" />
      </IntegrationMetadataStyled>
    );
  }

  return (
    <IntegrationMetadataStyled>
      {shouldShowMetadata && (
        <div data-testid="integration-metadata"> {getMetadata()}</div>
      )}
    </IntegrationMetadataStyled>
  );
};

export default IntegrationMetadata;
